"use client";

import Link from "next/link";
import { useTranslation } from "@/lib/i18n/context";
import { CONTACT_EMAIL } from "@/lib/seo/config";
import { BUSINESS } from "@/lib/legal/business";

/**
 * Real contact page (replaces the placeholder) — gives crawlers and visitors a
 * clear contact path plus the legal business identity (an E-E-A-T trust signal
 * for a paid SaaS).
 */
export function ContactContent() {
  const { lang } = useTranslation();
  const isSl = lang !== "en";

  const copy = isSl
    ? {
        eyebrow: "POMOČ · KONTAKT",
        h1a: "Pišite nam — ",
        h1em: "z veseljem pomagamo",
        sub: "Vprašanje, predlog ali težava? Odgovorimo v roku enega delovnega dne.",
        emailLabel: "E-pošta",
        supportLabel: "Podpora v aplikaciji",
        supportText: "Prijavljeni uporabniki lahko odprejo zahtevek neposredno v aplikaciji.",
        supportCta: "Odpri podporo",
        businessLabel: "Upravljavec",
        responseNote: "Odgovorimo v roku enega delovnega dne.",
      }
    : {
        eyebrow: "HELP · CONTACT",
        h1a: "Get in touch — ",
        h1em: "we're glad to help",
        sub: "A question, an idea, or a problem? We reply within one business day.",
        emailLabel: "Email",
        supportLabel: "In-app support",
        supportText: "Signed-in users can open a request directly inside the app.",
        supportCta: "Open support",
        businessLabel: "Operator",
        responseNote: "We reply within one business day.",
      };

  return (
    <main className="mx-auto max-w-[1280px] px-6 pb-32 pt-12 text-[var(--ink)]">
      <header className="mb-10 border-b border-[var(--rule)]/30 pb-6">
        <p className="mb-2.5 flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
          {copy.eyebrow}
        </p>
        <h1 className="font-display text-[clamp(40px,5vw,64px)] leading-[0.95] tracking-[-0.025em] text-balance">
          {copy.h1a}
          <em className="font-medium italic text-[var(--terracotta)]">{copy.h1em}</em>.
        </h1>
        <p className="mt-3 max-w-[640px] font-display text-[17px] italic leading-[1.5] text-[var(--ink-soft)]">
          {copy.sub}
        </p>
      </header>

      <div className="mx-auto grid max-w-[820px] gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-[var(--rule)] bg-[var(--paper-2)]/30 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
            {copy.emailLabel}
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="mt-1.5 block font-display text-[19px] text-[var(--terracotta)] underline-offset-2 hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
          <p className="mt-2 font-display text-[14.5px] leading-[1.6] text-[var(--ink-soft)]">
            {copy.responseNote}
          </p>
        </div>

        <div className="rounded-lg border border-[var(--rule)] bg-[var(--paper-2)]/30 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
            {copy.supportLabel}
          </p>
          <p className="mt-1.5 font-display text-[15px] leading-[1.6] text-[var(--ink-soft)]">
            {copy.supportText}
          </p>
          <Link
            href="/support"
            className="mt-2 inline-block font-mono text-[12px] uppercase tracking-[0.14em] text-[var(--ink-soft)] underline-offset-4 hover:text-[var(--terracotta)] hover:underline"
          >
            {copy.supportCta} →
          </Link>
        </div>
      </div>

      <div className="mx-auto mt-6 max-w-[820px] border-t border-[var(--rule)]/40 pt-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
          {copy.businessLabel}
        </p>
        <address className="mt-1.5 font-display text-[15px] not-italic leading-[1.7] text-[var(--ink-soft)]">
          {BUSINESS.name}
          <br />
          {BUSINESS.address}
          <br />
          {isSl ? "ID za DDV: " : "VAT: "}
          {BUSINESS.vatNumber}
        </address>
      </div>
    </main>
  );
}
